import type { MapView } from '../constructors';

/**
 * Diagnostic context gathered from the Esri MapView in the renderer. Assembled
 * and consumed entirely in the renderer — never crosses the IPC bridge — so it
 * lives here rather than in @m43/bridge-interface.
 */
export type MapDiagnosticContext = {
    fatalErrorCount: number;
    contextLossCount: number;
    contextRestoreCount: number;
    zoom?: number;
    isEmbeddedMap: boolean;
    layerCount: number;
    canvasCount: number;
};

let fatalErrorCount = 0;
let contextLossCount = 0;
let contextRestoreCount = 0;
let layerviewCreateErrorCount = 0;
let basemapLoadErrorCount = 0;
let mapErrorCount = 0;
let viewSuspendCount = 0;
let viewResumeCount = 0;
let lastContextLossTimestampMs: number | null = null;

export function incrementFatalErrorCount(): void {
    fatalErrorCount++;
}

export function incrementContextLossCount(): void {
    contextLossCount++;
    lastContextLossTimestampMs = Date.now();
}

export function incrementContextRestoreCount(): void {
    contextRestoreCount++;
}

export function incrementLayerviewCreateErrorCount(): void {
    layerviewCreateErrorCount++;
}

/** Esri suspends the view (and snapshots its render buffers) when its container has
 * ~zero area — which is what happens to the First Responder map while hidden. These
 * count the suspend/resume cycle so it can be correlated with black maps. */
export function incrementViewSuspendCount(): void {
    viewSuspendCount++;
}

export function incrementViewResumeCount(): void {
    viewResumeCount++;
}

export function incrementBasemapLoadErrorCount(): void {
    basemapLoadErrorCount++;
}

/** Catch-all for every `logMapError` call, so unexpected SDK/service failures still
 * show up as a confound on episode events even without a dedicated counter. */
export function incrementMapErrorCount(): void {
    mapErrorCount++;
}

export type MapErrorCounts = {
    fatalErrorCount: number;
    contextLossCount: number;
    contextRestoreCount: number;
    layerviewCreateErrorCount: number;
    basemapLoadErrorCount: number;
    mapErrorCount: number;
    viewSuspendCount: number;
    viewResumeCount: number;
    /** Detection lag: how long ago the browser told us the context was lost. A large
     * value on a failure event means the context died well before we declared the map
     * broken. Undefined when no `webglcontextlost` has fired this session. */
    msSinceContextLost?: number;
};

export function getMapErrorCounts(): MapErrorCounts {
    const counts: MapErrorCounts = {
        fatalErrorCount,
        contextLossCount,
        contextRestoreCount,
        layerviewCreateErrorCount,
        basemapLoadErrorCount,
        mapErrorCount,
        viewSuspendCount,
        viewResumeCount,
    };
    if (lastContextLossTimestampMs !== null) {
        counts.msSinceContextLost = Date.now() - lastContextLossTimestampMs;
    }
    return counts;
}

export function getMapDiagnosticContext(
    mapView: MapView | undefined,
    mapRef: React.MutableRefObject<HTMLDivElement | null>,
    isEmbeddedMap: boolean
): MapDiagnosticContext {
    const result: MapDiagnosticContext = {
        fatalErrorCount,
        contextLossCount,
        contextRestoreCount,
        isEmbeddedMap,
        layerCount: mapView?.map?.layers?.length ?? 0,
        canvasCount: mapRef.current?.querySelectorAll('canvas')?.length ?? 0,
    };
    if (mapView?.zoom != null) {
        result.zoom = mapView.zoom;
    }
    return result;
}
